/**
 * TreatyRedactor — Phase 14: per-treaty redaction expression evaluator.
 *
 * Treaties carry a JSON-Logic-style list of redaction rules. Each rule is a
 * pure-data expression (no JS eval): { field, action, params? }.
 *
 * Supported actions:
 *
 *   - 'drop'         remove the field entirely
 *   - 'coarsenGeo'   replace a location object with 2-decimal precision and
 *                    clamp accuracyM to a minimum (default 1000m). Only valid
 *                    on fields shaped {lat, lng, accuracyM, fixAt}.
 *   - 'hash'         replace with a 12-char BLAKE3 hex prefix of the original
 *                    value (any JSON-serializable value).
 *   - 'truncate'     keep first N chars of a string (default N=80). Non-strings
 *                    pass through unchanged.
 *
 * Rules are applied IN ORDER. Later rules see the result of earlier rules.
 *
 * Both the forwarder AND the receiver apply the treaty rules — receiver runs
 * the same rules as defense-in-depth in case the forwarder shipped pre-rule
 * data on purpose or by bug.
 *
 * Field paths use dot notation: 'incident.narrative', 'incident.location'.
 * Nested objects are supported up to the depth needed for ALDS payloads.
 */

import { blake3 } from '@noble/hashes/blake3';
import { bytesToHex } from '@noble/hashes/utils';

import type { TreatyRedactionAction, TreatyRedactionRule } from '../storage/schema';

export interface CoarseGeo {
  lat: number;
  lng: number;
  accuracyM: number;
  fixAt: string;
}

/**
 * Apply a list of redaction rules to a JSON-serializable payload.
 * Returns a deep-cloned copy with the rules applied. Mutates nothing.
 */
export function applyRedactionRules<T extends Record<string, unknown>>(
  payload: T,
  rules: readonly TreatyRedactionRule[] | undefined,
): T {
  if (rules === undefined || rules.length === 0) return deepClone(payload);
  let out = deepClone(payload) as Record<string, unknown>;
  for (const rule of rules) {
    out = applyOne(out, rule);
  }
  return out as T;
}

function applyOne(payload: Record<string, unknown>, rule: TreatyRedactionRule): Record<string, unknown> {
  const segments = rule.field.split('.');
  return mutatePath(payload, segments, (current) => transform(current, rule.action, rule.params));
}

/**
 * Walk into `payload` following `segments`, applying `transform` at the leaf.
 * When transform returns `DROP_SENTINEL`, the leaf is removed from its parent.
 */
const DROP_SENTINEL: unique symbol = Symbol('drop');

function mutatePath(
  payload: Record<string, unknown>,
  segments: string[],
  transformFn: (current: unknown) => unknown | typeof DROP_SENTINEL,
): Record<string, unknown> {
  if (segments.length === 0) return payload;
  const [head, ...rest] = segments;
  if (head === undefined) return payload;
  if (rest.length === 0) {
    const next = transformFn(payload[head]);
    if (next === DROP_SENTINEL) {
      const { [head]: _dropped, ...remaining } = payload;
      void _dropped;
      return remaining;
    }
    return { ...payload, [head]: next };
  }
  const child = payload[head];
  if (typeof child !== 'object' || child === null) return payload;  // no-op: path doesn't exist
  const updated = mutatePath(child as Record<string, unknown>, rest, transformFn);
  return { ...payload, [head]: updated };
}

function transform(value: unknown, action: TreatyRedactionAction, params: TreatyRedactionRule['params']): unknown | typeof DROP_SENTINEL {
  if (value === undefined) return value;  // can't transform something not there
  switch (action) {
    case 'drop':
      return DROP_SENTINEL;
    case 'hash':
      return hashValue(value);
    case 'truncate':
      return truncateValue(value, params?.maxLen ?? 80);
    case 'coarsenGeo':
      return coarsenGeoValue(value);
  }
}

function hashValue(value: unknown): string {
  const canonical = typeof value === 'string' ? value : JSON.stringify(value);
  return bytesToHex(blake3(new TextEncoder().encode(canonical))).slice(0, 12);
}

function truncateValue(value: unknown, maxLen: number): unknown {
  if (typeof value !== 'string') return value;
  if (value.length <= maxLen) return value;
  return value.slice(0, maxLen);
}

function coarsenGeoValue(value: unknown): unknown {
  if (typeof value !== 'object' || value === null) return value;
  const geo = value as Partial<CoarseGeo>;
  if (typeof geo.lat !== 'number' || typeof geo.lng !== 'number') return value;
  const accuracyM = typeof geo.accuracyM === 'number' ? Math.max(geo.accuracyM, 1000) : 1000;
  return {
    lat: Math.round(geo.lat * 100) / 100,
    lng: Math.round(geo.lng * 100) / 100,
    accuracyM,
    ...(typeof geo.fixAt === 'string' ? { fixAt: geo.fixAt } : {}),
  };
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Validate a list of rules without applying them. Returns an array of error
 * strings (empty if all rules are well-formed). The UI uses this to surface
 * treaty-builder validation errors before saving.
 */
export function validateRedactionRules(rules: readonly TreatyRedactionRule[]): string[] {
  const errors: string[] = [];
  const validActions: TreatyRedactionAction[] = ['drop', 'coarsenGeo', 'hash', 'truncate'];
  for (let i = 0; i < rules.length; i++) {
    const r = rules[i]!;
    if (typeof r.field !== 'string' || r.field.length === 0) {
      errors.push(`rule[${i}]: field must be a non-empty string`);
    }
    if (!validActions.includes(r.action)) {
      errors.push(`rule[${i}]: action '${r.action}' is not supported`);
    }
    if (r.params?.maxLen !== undefined && (!Number.isFinite(r.params.maxLen) || r.params.maxLen <= 0)) {
      errors.push(`rule[${i}]: params.maxLen must be a positive number`);
    }
  }
  return errors;
}
